import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import { useAuth } from '@/hooks/useAuth';
import {
  createPrivacyRequest,
  DATA_MINIMIZATION_MATRIX,
  exportSubjectData,
  fetchPrivacyStats,
  handlePrivacyRequest,
  listAccessLog,
  listPrivacyRequests,
  privacyStatusMeta,
  privacyTypeMeta,
  PRIVACY_REQUEST_TYPES,
  type CoreDataAccessLog,
  type CorePrivacyRequest,
} from '@/lib/corePrivacy';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function PrivacyTab({ org }: Props) {
  const { user } = useAuth();
  const [requests, setRequests] = useState<CorePrivacyRequest[]>([]);
  const [log, setLog] = useState<CoreDataAccessLog[]>([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, completed: 0, byType: {} as Record<string, number>, accessEvents: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState({ requestType: 'export', subjectType: 'user', subjectId: '', requesterName: '', requesterEmail: '', reason: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [r, l, s] = await Promise.all([listPrivacyRequests(org.id), listAccessLog(org.id), fetchPrivacyStats(org.id)]);
      setRequests(r);
      setLog(l);
      setStats(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    setSaving(true);
    setError('');
    setMsg('');
    try {
      await createPrivacyRequest({
        organizationId: org.id, tenantId: org.tenant_id,
        requestType: form.requestType, subjectType: form.subjectType,
        subjectId: form.subjectId || null, requesterUserId: user?.id ?? null,
        requesterName: form.requesterName || null, requesterEmail: form.requesterEmail || null,
        reason: form.reason || null,
      });
      setMsg('Demande enregistrée. Elle sera traitée selon les exigences applicables.');
      setForm({ ...form, subjectId: '', reason: '' });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Envoi impossible.');
    } finally {
      setSaving(false);
    }
  };

  const handle = async (id: number, status: 'in_progress' | 'completed' | 'rejected', subjectId?: string | null) => {
    try {
      let result: Record<string, unknown> | null = null;
      const req = requests.find((r) => r.id === id);
      if (status === 'completed' && req && (req.request_type === 'export' || req.request_type === 'access') && subjectId) {
        result = await exportSubjectData(org.id, subjectId);
      }
      await handlePrivacyRequest(id, status, result, user?.id ?? null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur.');
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Vie privée</span> : architecture respectant la <span className="font-medium text-foreground-600">minimisation des données</span> —
        contrôle des accès, journalisation, export lorsque requis, suppression lorsque applicable, gestion des consentements.
        Les exigences légales précises sont gérées selon le pays et les modules.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}
      {msg && <div className="p-3 bg-primary-50 border border-primary-200 rounded-md text-xs text-primary-800 flex items-center gap-2"><i className="ri-checkbox-circle-line"></i>{msg}</div>}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-shield-user-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.total}</p>
          <p className="text-xs text-foreground-500">Demandes</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-time-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.pending}</p>
          <p className="text-xs text-foreground-500">En attente</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-checkbox-circle-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.completed}</p>
          <p className="text-xs text-foreground-500">Traitées</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-eye-line text-lg text-secondary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.accessEvents}</p>
          <p className="text-xs text-foreground-500">Accès journalisés</p>
        </div>
      </div>

      {/* Minimisation */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center"><i className="ri-focus-3-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Minimisation — accès par module</h4>
        </div>
        <div className="divide-y divide-background-200/60">
          {DATA_MINIMIZATION_MATRIX.map((m) => (
            <div key={m.module} className="flex items-start gap-3 px-4 py-3">
              <span className="w-9 h-9 rounded-lg bg-background-100 text-foreground-600 flex items-center justify-center flex-shrink-0"><i className={m.icon}></i></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground-800">{m.module}</p>
                <p className="text-[11px] text-primary-700 mt-0.5"><i className="ri-check-line"></i> {m.allowed}</p>
                <p className="text-[11px] text-foreground-400"><i className="ri-close-line"></i> {m.restricted}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Nouvelle demande */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-add-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Nouvelle demande</h4>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex flex-wrap gap-2">
            {PRIVACY_REQUEST_TYPES.map((t) => (
              <button key={t.key} onClick={() => setForm({ ...form, requestType: t.key })} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer flex items-center gap-1.5 ${form.requestType === t.key ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-700 hover:bg-background-200/70'}`}>
                <i className={t.icon}></i>{t.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-foreground-400">{privacyTypeMeta(form.requestType).desc}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <select className={INPUT} value={form.subjectType} onChange={(e) => setForm({ ...form, subjectType: e.target.value })}>
              <option value="user">Utilisateur</option>
              <option value="customer">Client</option>
              <option value="contact">Contact</option>
            </select>
            <input className={INPUT} placeholder="Identifiant du sujet" value={form.subjectId} onChange={(e) => setForm({ ...form, subjectId: e.target.value })} />
            <input className={INPUT} placeholder="Nom du demandeur" value={form.requesterName} onChange={(e) => setForm({ ...form, requesterName: e.target.value })} />
            <input className={INPUT} placeholder="Email du demandeur" value={form.requesterEmail} onChange={(e) => setForm({ ...form, requesterEmail: e.target.value })} />
          </div>
          <input className={INPUT} placeholder="Motif (optionnel)" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} maxLength={300} />
          <button onClick={submit} disabled={saving} className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-send-plane-line"></i>} Enregistrer la demande
          </button>
        </div>
      </div>

      {/* Demandes */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center"><i className="ri-file-shield-2-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Demandes de confidentialité</h4>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : requests.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucune demande.</p>
        ) : (
          <div className="divide-y divide-background-200/60">
            {requests.map((r) => {
              const tm = privacyTypeMeta(r.request_type);
              const sm = privacyStatusMeta(r.status);
              return (
                <div key={r.id} className="flex items-center gap-3 px-4 py-3">
                  <span className="w-9 h-9 rounded-lg bg-background-100 text-foreground-600 flex items-center justify-center flex-shrink-0"><i className={tm.icon}></i></span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-foreground-800 truncate">{tm.label} · {r.subject_type} #{r.subject_id || '—'}</p>
                    <p className="text-[11px] text-foreground-400 truncate">
                      {r.requester_name || '—'}{r.reason ? ` · ${r.reason}` : ''} · {r.created_at ? new Date(r.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : ''}
                    </p>
                  </div>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full whitespace-nowrap ${sm.className}`}>{sm.label}</span>
                  {(r.status === 'pending' || r.status === 'in_progress') && (
                    <div className="flex gap-1.5">
                      {r.status === 'pending' && (
                        <button onClick={() => handle(r.id, 'in_progress')} className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-[11px] font-medium whitespace-nowrap cursor-pointer">Traiter</button>
                      )}
                      <button onClick={() => handle(r.id, 'completed', r.subject_id)} className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-900 text-[11px] font-medium whitespace-nowrap cursor-pointer">Terminer</button>
                      <button onClick={() => handle(r.id, 'rejected')} className="px-2.5 py-1 rounded-full bg-red-100 text-red-800 text-[11px] font-medium whitespace-nowrap cursor-pointer">Refuser</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Journal d'accès */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center"><i className="ri-eye-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Journal des accès aux données</h4>
        </div>
        {log.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucun accès journalisé.</p>
        ) : (
          <div className="divide-y divide-background-200/60 max-h-[300px] overflow-y-auto">
            {log.map((l) => (
              <div key={l.id} className="flex items-center gap-3 px-4 py-2.5">
                <i className="ri-database-2-line text-foreground-400"></i>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-foreground-800 truncate">{l.module} · {l.action} · {l.resource_type}{l.resource_id ? ` #${l.resource_id}` : ''}</p>
                  <p className="text-[11px] text-foreground-400 truncate">{l.purpose || '—'} · {l.created_at ? new Date(l.created_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}